import Phaser from 'phaser';
import { ABILITY_RULES, BUILDINGS, CELL_SIZE, GRID_SIZE, LOGICAL_BOARD_SIZE, MAPS, STRUCTURE_FOOTPRINT_CELLS, TOWERS, isTowerId } from '../../game/content/data';
import { BUILDING_UPGRADES, resolveTowerDefinition } from '../../game/content/upgrades';
import type { AbilityId, CatalogueId, MapDefinition } from '../../game/content/types';
import type { BarbedWireState, BuildingState, DeployedStructureSelection, EnemyState, MineState, ProjectileState, TowerState, VisualEvent, WallState } from '../../game/simulation/types';
import { getController } from '../bridge';
import { findHoveredTower } from '../view/towerHover';
import { VisualEventRunGate } from '../view/visualEventRunGate';
import { weaponFxFor } from '../view/weaponFx';

const textureNoise = (x: number, y: number, salt: number): number => {
  let value = (Math.imul(x + salt * 37, 374761393) + Math.imul(y + salt * 17, 668265263)) >>> 0;
  value = Math.imul(value ^ (value >>> 13), 1274126177) >>> 0;
  return (value ^ (value >>> 16)) / 0xffffffff;
};

interface ActiveVisualEffect {
  event: VisualEvent;
  startedAt: number;
  lifetime: number;
}

/** Thin Phaser view: it only renders the deterministic simulation and forwards pointer input. */
export class BattleScene extends Phaser.Scene {
  private terrainBase!: Phaser.GameObjects.Graphics;
  private sandTexture!: Phaser.GameObjects.TileSprite;
  private rockBase!: Phaser.GameObjects.Graphics;
  private rockTexture!: Phaser.GameObjects.TileSprite;
  private graphics!: Phaser.GameObjects.Graphics;
  private fxGraphics!: Phaser.GameObjects.Graphics;
  private sprites!: Phaser.GameObjects.Container;
  private rockMaskGraphics: Phaser.GameObjects.Graphics | null = null;
  private terrainMapId: MapDefinition['id'] | null = null;
  private viewTime = 0;
  private visualEffects: ActiveVisualEffect[] = [];
  private visualEventRun = new VisualEventRunGate<object>();

  constructor() { super('battle'); }

  create(): void {
    this.terrainBase = this.add.graphics().setDepth(-4);
    this.sandTexture = this.add.tileSprite(0, 0, LOGICAL_BOARD_SIZE, LOGICAL_BOARD_SIZE, 'terrain-sand')
      .setOrigin(0).setTileScale(LOGICAL_BOARD_SIZE / 512).setTint(0xf7edcb).setAlpha(0.3).setDepth(-3);
    this.rockBase = this.add.graphics().setDepth(-2);
    this.rockTexture = this.add.tileSprite(0, 0, LOGICAL_BOARD_SIZE, LOGICAL_BOARD_SIZE, 'terrain-rock')
      .setOrigin(0).setTileScale(LOGICAL_BOARD_SIZE / 512).setTint(0xc5c7b4).setAlpha(0.36).setDepth(-1);
    this.graphics = this.add.graphics().setDepth(0);
    this.sprites = this.add.container(0, 0).setDepth(1);
    this.fxGraphics = this.add.graphics().setDepth(2);
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => getController().setHover(pointer.x, pointer.y));
    this.input.on('pointerout', () => getController().clearHover());
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => getController().boardPointer(pointer.x, pointer.y));
  }

  update(_time: number, delta: number): void {
    this.viewTime += delta;
    const controller = getController();
    controller.advance(delta);
    this.drawFrame();
  }

  private drawFrame(): void {
    const controller = getController();
    const render = controller.getRenderState();
    const simulation = render.sim;
    const map = MAPS[simulation?.getState().mapId ?? render.selectedMap];
    this.graphics.clear();
    this.fxGraphics.clear();
    this.sprites.removeAll(true);
    this.drawMap(map);
    if (!simulation || render.screen !== 'board') return;
    const state = simulation.getState();
    if (this.visualEventRun.begin(simulation)) this.visualEffects = [];
    this.syncVisualEvents(simulation.getSnapshot().visualEvents);
    if (render.mode.kind === 'idle' && !render.selectedNode) this.drawHoveredTowerRange(render.hover, state.towers);
    if (render.mode.kind === 'build') this.drawPlacement(render.hover, render.mode.structureId);
    if (render.mode.kind === 'ability') this.drawAbilityPreview(render.hover, render.mode.ability);
    if (render.selectedNode) this.drawSelectedStructure(render.selectedNode, state.towers, state.buildings);
    this.drawWalls(state.walls);
    this.drawMines(state.mines);
    this.drawBarbedWires(state.barbedWires);
    this.drawTowers(state.towers);
    this.drawTargetLocks(state.towers, state.enemies);
    this.drawBuildings(state.buildings);
    this.drawEnemies(state.enemies);
    this.drawProjectiles(state.projectiles);
    this.drawVisualEffects();
    if (state.paused) {
      this.graphics.fillStyle(0x080808, 0.56);
      this.graphics.fillRect(0, 0, LOGICAL_BOARD_SIZE, LOGICAL_BOARD_SIZE);
    }
  }

  private drawMap(map: MapDefinition): void {
    const graphics = this.graphics;
    this.updateTerrainMask(map);

    // Texture is presentation-only: the exact cell union below is the sole rock mask used by placement and navigation.
    // A very light board wash keeps both supplied materials near the muted CD1 capture rather than overpowering sprites.
    graphics.fillStyle(map.palette.terrain, 0.05);
    graphics.fillRect(0, 0, LOGICAL_BOARD_SIZE, LOGICAL_BOARD_SIZE);

    // Every exposed mask edge receives a soft sand nibble, a dark contact shadow on the basin, and an ochre lit rim.
    for (let y = 0; y < GRID_SIZE; y += 1) {
      for (let x = 0; x < GRID_SIZE; x += 1) {
        if (!this.isRockCell(map, x, y)) continue;
        if (!this.isRockCell(map, x, y - 1)) this.drawShelfEdge(x, y, 0, -1);
        if (!this.isRockCell(map, x + 1, y)) this.drawShelfEdge(x, y, 1, 0);
        if (!this.isRockCell(map, x, y + 1)) this.drawShelfEdge(x, y, 0, 1);
        if (!this.isRockCell(map, x - 1, y)) this.drawShelfEdge(x, y, -1, 0);
      }
    }
  }

  private isRockCell(map: MapDefinition, x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < GRID_SIZE && y < GRID_SIZE && map.surfaceRows[y]?.[x] === 'T';
  }

  private updateTerrainMask(map: MapDefinition): void {
    if (this.terrainMapId === map.id) return;
    this.terrainMapId = map.id;
    this.sandTexture.setTint(map.id === 'map-3' ? 0xf0e2bd : 0xf7edcb);
    this.terrainBase.clear();
    this.terrainBase.fillStyle(map.palette.terrain, 1);
    this.terrainBase.fillRect(0, 0, LOGICAL_BOARD_SIZE, LOGICAL_BOARD_SIZE);
    this.rockBase.clear();
    this.rockTexture.clearMask(true);
    this.rockMaskGraphics?.destroy();
    const maskGraphics = this.make.graphics({ x: 0, y: 0 });
    for (let y = 0; y < GRID_SIZE; y += 1) {
      for (let x = 0; x < GRID_SIZE; x += 1) {
        if (!this.isRockCell(map, x, y)) continue;
        this.rockBase.fillStyle(map.palette.rock, 1);
        this.rockBase.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        maskGraphics.fillStyle(0xffffff, 1);
        maskGraphics.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
      }
    }
    this.rockMaskGraphics = maskGraphics;
    this.rockTexture.setMask(maskGraphics.createGeometryMask());
  }

  private drawShelfEdge(cellX: number, cellY: number, normalX: number, normalY: number): void {
    const graphics = this.graphics;
    const edgeX = cellX * CELL_SIZE + (normalX > 0 ? CELL_SIZE : 0);
    const edgeY = cellY * CELL_SIZE + (normalY > 0 ? CELL_SIZE : 0);
    const tangentX = normalY === 0 ? 0 : 1;
    const tangentY = normalX === 0 ? 0 : 1;
    const seed = textureNoise(cellX, cellY, normalX * 7 + normalY * 11 + 9);
    const inset = 0.7 + seed * 0.5;
    const shadowOffset = 2.2 + seed * 0.8;
    const startX = edgeX;
    const startY = edgeY;
    const endX = edgeX + tangentX * CELL_SIZE;
    const endY = edgeY + tangentY * CELL_SIZE;

    // Continuous low-contrast bands describe the raised shelf. Their joins are intentionally clean: the former
    // per-segment sand bites made the silhouette read as a zipper rather than a geological edge.
    graphics.lineStyle(3, 0x3a2112, 0.31);
    graphics.lineBetween(startX + normalX * shadowOffset, startY + normalY * shadowOffset, endX + normalX * shadowOffset, endY + normalY * shadowOffset);
    graphics.lineStyle(1, 0xd49a56, 0.45);
    graphics.lineBetween(startX - normalX * inset, startY - normalY * inset, endX - normalX * inset, endY - normalY * inset);

    // A few inward-facing fracture ticks provide edge variation without breaking the exact mask silhouette.
    if (seed > 0.78) {
      const along = 8 + textureNoise(cellX, cellY, 19) * 15;
      const crackX = edgeX + tangentX * along - normalX * 3;
      const crackY = edgeY + tangentY * along - normalY * 3;
      graphics.lineStyle(1, 0x2b190e, 0.3);
      graphics.lineBetween(crackX, crackY, crackX - normalX * 5 + tangentX * 2, crackY - normalY * 5 + tangentY * 2);
    }
  }

  private drawPlacement(hover: { x: number; y: number } | null, structureId: CatalogueId): void {
    const graphics = this.graphics;
    if (!hover) return;
    const cellX = Math.floor(hover.x / CELL_SIZE);
    const cellY = Math.floor(hover.y / CELL_SIZE);
    const legal = getController().getSimulation()?.canPlaceStructure(structureId, hover.x, hover.y) ?? false;
    const [width, height] = STRUCTURE_FOOTPRINT_CELLS[structureId];
    const center = { x: cellX * CELL_SIZE + width * CELL_SIZE / 2, y: cellY * CELL_SIZE + height * CELL_SIZE / 2 };
    graphics.fillStyle(legal ? 0x55b7d8 : 0xde5141, 0.20);
    graphics.fillRect(cellX * CELL_SIZE + 2, cellY * CELL_SIZE + 2, width * CELL_SIZE - 4, height * CELL_SIZE - 4);
    if (isTowerId(structureId)) {
      const definition = TOWERS[structureId];
      graphics.lineStyle(3, 0x4b9cc5, 0.72);
      graphics.strokeCircle(center.x, center.y, getController().getSimulation()?.getEffectiveTowerRange(structureId) ?? definition.range.value);
      this.drawTower({ id: 0, definitionId: structureId, position: center, cell: { x: cellX, y: cellY }, angle: -Math.PI / 2, cooldownTicks: 0, targetId: null, cost: definition.cost.value }, 0.58);
    } else {
      this.drawBuilding({ id: 0, definitionId: structureId, cell: { x: cellX, y: cellY }, cost: BUILDINGS[structureId].cost.value }, 0.58);
    }
  }

  private drawHoveredTowerRange(hover: { x: number; y: number } | null, towers: readonly TowerState[]): void {
    const simulation = getController().getSimulation();
    const tower = findHoveredTower(towers, hover);
    if (!simulation || !tower) return;
    const radius = simulation.getEffectiveTowerRange(tower.definitionId, tower.upgradeId);
    this.graphics.fillStyle(0x55b7d8, 0.04);
    this.graphics.fillCircle(tower.position.x, tower.position.y, radius);
    this.graphics.lineStyle(2, 0x66b9d8, 0.68);
    this.graphics.strokeCircle(tower.position.x, tower.position.y, radius);
  }

  private drawSelectedStructure(
    selected: DeployedStructureSelection,
    towers: readonly TowerState[],
    buildings: readonly BuildingState[],
  ): void {
    this.graphics.fillStyle(0xffdc54, 0.12);
    this.graphics.lineStyle(2, 0xffdc54, 0.92);
    if (selected.kind === 'tower') {
      const tower = towers.find((candidate) => candidate.id === selected.id);
      if (!tower) return;
      const radius = getController().getSimulation()?.getEffectiveTowerRange(tower.definitionId, tower.upgradeId)
        ?? resolveTowerDefinition(tower.definitionId, tower.upgradeId).range.value;
      this.graphics.fillRect(tower.cell.x * CELL_SIZE + 2, tower.cell.y * CELL_SIZE + 2, CELL_SIZE - 4, CELL_SIZE - 4);
      this.graphics.strokeRect(tower.cell.x * CELL_SIZE + 2, tower.cell.y * CELL_SIZE + 2, CELL_SIZE - 4, CELL_SIZE - 4);
      this.graphics.fillStyle(0x55b7d8, 0.04);
      this.graphics.fillCircle(tower.position.x, tower.position.y, radius);
      this.graphics.lineStyle(2, 0x66b9d8, 0.72);
      this.graphics.strokeCircle(tower.position.x, tower.position.y, radius);
      return;
    }
    const building = buildings.find((candidate) => candidate.id === selected.id);
    if (!building) return;
    const [width, height] = STRUCTURE_FOOTPRINT_CELLS[building.definitionId];
    this.graphics.fillRect(building.cell.x * CELL_SIZE + 2, building.cell.y * CELL_SIZE + 2, width * CELL_SIZE - 4, height * CELL_SIZE - 4);
    this.graphics.strokeRect(building.cell.x * CELL_SIZE + 2, building.cell.y * CELL_SIZE + 2, width * CELL_SIZE - 4, height * CELL_SIZE - 4);
  }

  private drawAbilityPreview(hover: { x: number; y: number } | null, ability: AbilityId): void {
    if (!hover) return;
    const graphics = this.graphics;
    if (ability === 'wall') {
      const cellX = Math.floor(hover.x / CELL_SIZE);
      const cellY = Math.floor(hover.y / CELL_SIZE);
      const legal = getController().getSimulation()?.canPlaceWall(hover.x, hover.y) ?? false;
      graphics.fillStyle(legal ? 0x7fbd79 : 0xde5141, 0.28);
      graphics.fillRect(cellX * CELL_SIZE + 2, cellY * CELL_SIZE + 4, CELL_SIZE - 4, CELL_SIZE - 8);
      graphics.lineStyle(2, legal ? 0xd7e4c8 : 0xff9a89, 0.95);
      graphics.strokeRect(cellX * CELL_SIZE + 3, cellY * CELL_SIZE + 5, CELL_SIZE - 6, CELL_SIZE - 10);
      return;
    }
    if (ability === 'mine' || ability === 'barbed-wire') {
      const cellX = Math.floor(hover.x / CELL_SIZE);
      const cellY = Math.floor(hover.y / CELL_SIZE);
      const simulation = getController().getSimulation();
      const legal = ability === 'mine' ? simulation?.canPlaceMine(hover.x, hover.y) : simulation?.canPlaceBarbedWire(hover.x, hover.y);
      const color = legal ? 0xe2c45f : 0xde5141;
      graphics.fillStyle(color, 0.22);
      graphics.fillRect(cellX * CELL_SIZE + 2, cellY * CELL_SIZE + 2, CELL_SIZE - 4, CELL_SIZE - 4);
      graphics.lineStyle(2, color, 0.92);
      graphics.strokeRect(cellX * CELL_SIZE + 2, cellY * CELL_SIZE + 2, CELL_SIZE - 4, CELL_SIZE - 4);
      const preview = this.add.image(cellX * CELL_SIZE + CELL_SIZE / 2, cellY * CELL_SIZE + CELL_SIZE / 2, `ability-${ability}`)
        .setDisplaySize(28, 28).setAlpha(0.68);
      this.sprites.add(preview);
      return;
    }
    const rule = ABILITY_RULES[ability];
    const radius = 'radius' in rule ? rule.radius : 0;
    const color = ability === 'missile' ? 0xffcf63 : 0x77d8ff;
    graphics.fillStyle(color, 0.10);
    graphics.fillCircle(hover.x, hover.y, radius);
    graphics.lineStyle(2, color, 0.9);
    graphics.strokeCircle(hover.x, hover.y, radius);
    graphics.lineBetween(hover.x - 7, hover.y, hover.x + 7, hover.y);
    graphics.lineBetween(hover.x, hover.y - 7, hover.x, hover.y + 7);
  }

  private drawTowers(towers: readonly TowerState[]): void { towers.forEach((tower) => this.drawTower(tower, 1)); }

  private drawTargetLocks(towers: readonly TowerState[], enemies: readonly EnemyState[]): void {
    towers.forEach((tower) => {
      if (tower.upgradeId !== 'gauss.hammer' || tower.lockTargetId == null || (tower.lockTicks ?? 0) <= 0) return;
      const target = enemies.find((enemy) => enemy.id === tower.lockTargetId && enemy.hp > 0);
      if (!target) return;
      const required = resolveTowerDefinition(tower.definitionId, tower.upgradeId).targetLockTicks ?? 1;
      const progress = Math.min(1, (tower.lockTicks ?? 0) / required);
      this.fxGraphics.lineStyle(1, 0xff554c, 0.35 + progress * 0.55);
      this.fxGraphics.lineBetween(tower.position.x, tower.position.y, target.position.x, target.position.y);
      this.fxGraphics.strokeCircle(target.position.x, target.position.y, 11 - progress * 5);
      this.fxGraphics.fillStyle(0xff8a54, 0.45 + progress * 0.45);
      this.fxGraphics.fillCircle(target.position.x, target.position.y, 1.5);
    });
  }

  private drawBuildings(buildings: readonly BuildingState[]): void { buildings.forEach((building) => this.drawBuilding(building, 1)); }

  private drawWalls(walls: readonly WallState[]): void {
    walls.forEach((wall) => {
      const x = wall.cell.x * CELL_SIZE;
      const y = wall.cell.y * CELL_SIZE;
      this.graphics.fillStyle(0x49504c, 1);
      this.graphics.fillRect(x + 2, y + 4, CELL_SIZE - 4, CELL_SIZE - 8);
      this.graphics.lineStyle(2, 0xbdc4b2, 0.9);
      this.graphics.strokeRect(x + 3, y + 5, CELL_SIZE - 6, CELL_SIZE - 10);
    });
  }

  private drawMines(mines: readonly MineState[]): void {
    mines.forEach((mine) => {
      const x = mine.cell.x * CELL_SIZE + CELL_SIZE / 2;
      const y = mine.cell.y * CELL_SIZE + CELL_SIZE / 2;
      const armed = mine.fuseTicks !== undefined;
      const image = this.add.image(x, y, 'ability-mine').setDisplaySize(28, 28);
      if (armed) image.setTint(Math.floor(this.viewTime / 100) % 2 === 0 ? 0xff6a45 : 0xffffff);
      this.sprites.add(image);
    });
  }

  private drawBarbedWires(wires: readonly BarbedWireState[]): void {
    wires.forEach((wire) => {
      const x = wire.cell.x * CELL_SIZE + CELL_SIZE / 2;
      const y = wire.cell.y * CELL_SIZE + CELL_SIZE / 2;
      const image = this.add.image(x, y, 'ability-barbed-wire').setDisplaySize(30, 30)
        .setAlpha(0.55 + 0.45 * wire.usesRemaining / 20);
      this.sprites.add(image);
    });
  }

  private drawBuilding(building: BuildingState, alpha: number): void {
    const upgradeAsset = building.upgradeId ? BUILDING_UPGRADES[building.upgradeId]?.moduleAsset : undefined;
    const texture = upgradeAsset
      ? upgradeAsset.split('/').at(-1)?.replace(/\.png$/u, '') ?? `building-${building.definitionId.replace('cd1.building.', '')}`
      : `building-${building.definitionId.replace('cd1.building.', '')}`;
    const image = this.add.image(building.cell.x * CELL_SIZE + CELL_SIZE, building.cell.y * CELL_SIZE + CELL_SIZE, texture);
    image.setDisplaySize(56, 56).setAlpha(alpha);
    this.sprites.add(image);
  }

  private drawTower(tower: TowerState, alpha: number): void {
    const { x, y } = tower.position;
    const name = tower.definitionId.replace('cd1.tower.', '');
    const definition = resolveTowerDefinition(tower.definitionId, tower.upgradeId);
    const upgradeTexture = definition.upgradeId ? `tower-upgrade-${definition.upgradeId.replace('.', '-')}` : null;
    const directional = name === 'missile' || name === 'howitzer' || name === 'gatling' || name === 'aa'
      || name === 'goo' || name === 'chain' || name === 'gauss' || name === 'oil';
    if (directional) {
      const base = this.add.image(x, y, `tower-${name}-base`).setDisplaySize(31, 31).setAlpha(alpha);
      const recoil = this.recoilFor(tower.id);
      const module = this.add.image(x - Math.cos(tower.angle) * recoil, y - Math.sin(tower.angle) * recoil, upgradeTexture ?? `tower-${name}-module`)
        .setDisplaySize(31, 31).setRotation(tower.angle + Math.PI / 2).setAlpha(alpha);
      this.sprites.add([base, module]);
      return;
    }
    // Tesla and Nulificator are fixed radial composites rather than rotating turrets.
    const image = this.add.image(x, y, `tower-${name}`);
    image.setDisplaySize(31, 31).setAlpha(alpha);
    this.sprites.add(image);
    if (upgradeTexture) {
      const module = this.add.image(x, y, upgradeTexture).setDisplaySize(31, 31).setAlpha(alpha);
      this.sprites.add(module);
    }
  }

  private drawEnemies(enemies: readonly EnemyState[]): void {
    enemies.forEach((enemy) => {
      const { x, y } = enemy.position;
      const size = enemy.size === 'tiny' ? 14 : enemy.size === 'small' ? 19 : enemy.size === 'medium' ? 24 : 29;
      const key = `enemy-${enemy.domain}-${enemy.size}`;
      const image = this.add.image(x, y, key).setDisplaySize(size, size).setRotation(enemy.heading + Math.PI / 2).setAlpha(enemy.stoppedTicks > 0 ? 0.55 : 1);
      if (enemy.effect === 'shield') image.setTint(0xbad8ff);
      else if (enemy.effect === 'autorepair') image.setTint(0xa7e993);
      else if (enemy.effect === 'fast') image.setTint(0xffd28a);
      this.sprites.add(image);
      const graphics = this.graphics;
      graphics.fillStyle(0x151515, 0.75);
      graphics.fillRect(x - 10, y - 14, 20, 2);
      graphics.fillStyle(enemy.effect === 'shield' ? 0x97b9ff : 0x73d476, 1);
      graphics.fillRect(x - 10, y - 14, 20 * Math.max(0, enemy.hp) / enemy.maxHp, 2);
      this.drawEnemyStatus(enemy);
    });
  }

  private drawProjectiles(projectiles: readonly ProjectileState[]): void {
    const graphics = this.graphics;
    projectiles.forEach((projectile) => {
      const style = weaponFxFor(projectile.kind).style;
      const directionX = Math.cos(projectile.angle);
      const directionY = Math.sin(projectile.angle);
      const normalX = -directionY;
      const normalY = directionX;
      const { x, y } = projectile.position;
      if (style === 'cruise') {
        const tailX = x - directionX * 11;
        const tailY = y - directionY * 11;
        graphics.lineStyle(4, 0x5d4637, 0.28);
        graphics.lineBetween(x - directionX * 24, y - directionY * 24, tailX, tailY);
        graphics.fillStyle(0xff8a32, 0.88);
        graphics.fillCircle(tailX - directionX * 3 + normalX * 1.8, tailY - directionY * 3 + normalY * 1.8, 2.2);
        graphics.fillCircle(tailX - directionX * 3 - normalX * 1.8, tailY - directionY * 3 - normalY * 1.8, 2.2);
        graphics.lineStyle(3, 0xd7d0b4, 0.98);
        graphics.lineBetween(tailX, tailY, x + directionX * 5, y + directionY * 5);
        graphics.lineStyle(2, 0x6e745f, 0.95);
        graphics.lineBetween(x - directionX * 2 + normalX * 6, y - directionY * 2 + normalY * 6, x - directionX * 2 - normalX * 6, y - directionY * 2 - normalY * 6);
        graphics.fillStyle(0xffe8a0, 1);
        graphics.fillTriangle(
          x + directionX * 7,
          y + directionY * 7,
          x + normalX * 2,
          y + normalY * 2,
          x - normalX * 2,
          y - normalY * 2,
        );
        return;
      }
      if (style === 'missile' || style === 'rocket') {
        const length = style === 'missile' ? 20 : 10;
        graphics.lineStyle(style === 'missile' ? 3 : 2, 0xff9e46, 0.6);
        graphics.lineBetween(x - directionX * length, y - directionY * length, x, y);
        graphics.fillStyle(0xffe15b, 0.98);
        graphics.fillCircle(x, y, style === 'missile' ? 4 : 3);
        graphics.fillStyle(0xffffff, 0.9);
        graphics.fillCircle(x + directionX, y + directionY, 1.3);
        return;
      }
      if (style === 'howitzer') {
        [9, 16, 23].forEach((distance, index) => {
          graphics.fillStyle(index === 0 ? 0xd8c6a3 : 0x8b8170, 0.38 - index * 0.08);
          graphics.fillCircle(x - directionX * distance, y - directionY * distance, 3.2 - index * 0.5);
        });
        graphics.lineStyle(3, 0x4b4138, 1);
        graphics.lineBetween(x - directionX * 5, y - directionY * 5, x + directionX * 5, y + directionY * 5);
        graphics.lineStyle(1, 0xffd37a, 0.92);
        graphics.lineBetween(x - directionX * 2, y - directionY * 2, x + directionX * 5, y + directionY * 5);
        graphics.fillStyle(0x2e2925, 1);
        graphics.fillCircle(x + directionX * 5, y + directionY * 5, 2.6);
        return;
      }
      if (style === 'mortar') {
        const lift = 8 + Math.sin(this.viewTime * 0.018 + projectile.id) * 1.5;
        graphics.fillStyle(0x211d19, 0.28);
        graphics.fillEllipse(x + 4, y + 5, 9, 4);
        graphics.lineStyle(2, 0xe9c06b, 0.32);
        graphics.lineBetween(x - directionX * 18, y - directionY * 18, x, y - lift);
        graphics.fillStyle(0x54483a, 1);
        graphics.fillEllipse(x, y - lift, 7, 10);
        graphics.lineStyle(1, 0xffcb69, 0.95);
        graphics.strokeEllipse(x, y - lift, 6, 9);
        graphics.fillStyle(0xffe2a4, 0.9);
        graphics.fillCircle(x + directionX * 2, y - lift + directionY * 2, 1.4);
        return;
      }
      if (style === 'goo') {
        graphics.lineStyle(3, 0x71bd47, 0.46);
        graphics.lineBetween(x - directionX * 14, y - directionY * 14, x, y);
        graphics.fillStyle(0x9ee86b, 0.96);
        graphics.fillCircle(x, y, 4);
        graphics.fillStyle(0xe7ffbd, 0.82);
        graphics.fillCircle(x - directionX * 2, y - directionY * 2, 1.2);
        return;
      }
      if (style === 'oil') {
        graphics.lineStyle(3, 0xdd9b39, 0.5);
        graphics.lineBetween(x - directionX * 11, y - directionY * 11, x, y);
        graphics.fillStyle(0xf0b956, 0.94);
        graphics.fillCircle(x, y, 3.5);
        return;
      }
      if (style === 'nulificator') {
        graphics.lineStyle(2, 0xca79ff, 0.75);
        graphics.strokeCircle(x, y, 4 + Math.sin(this.viewTime / 34) * 1.2);
        graphics.fillStyle(0x8f49c6, 0.92);
        graphics.fillCircle(x, y, 2.4);
        return;
      }
      if (style === 'chain') {
        this.drawZap(graphics, x - directionX * 10, y - directionY * 10, x, y, projectile.color, 0.75, projectile.id, 1.5);
        graphics.fillStyle(0xf8f3b9, 0.94);
        graphics.fillCircle(x, y, 2.5);
        return;
      }
      graphics.lineStyle(2, projectile.color, 0.55);
      graphics.lineBetween(x - directionX * 7, y - directionY * 7, x, y);
      graphics.fillStyle(projectile.color, 0.95);
      graphics.fillCircle(x, y, 3);
      graphics.fillStyle(0xffffff, 0.9);
      graphics.fillCircle(x, y, 1);
    });
  }

  private drawEnemyStatus(enemy: EnemyState): void {
    const graphics = this.fxGraphics;
    const { x, y } = enemy.position;
    if (enemy.slowTicks > 0) {
      graphics.lineStyle(1, 0x68dc76, 0.82);
      graphics.strokeCircle(x, y, 10);
      graphics.fillStyle(0x68dc76, 0.9);
      graphics.fillCircle(x + 8, y - 9, 2);
    }
    if ((enemy.corrosionStacks?.length ?? 0) > 0) {
      graphics.lineStyle(1, 0xc7e83f, 0.88);
      graphics.strokeCircle(x, y, 11);
      graphics.fillStyle(0xc7e83f, 0.95);
      graphics.fillCircle(x - 8, y - 9, 2);
      graphics.fillCircle(x - 4, y - 12, 1);
    }
    if ((enemy.burningStacks?.length ?? 0) > 0) {
      graphics.lineStyle(1, 0xff7b30, 0.9);
      graphics.strokeCircle(x, y, 12);
      graphics.fillStyle(0xffb13b, 0.95);
      graphics.fillTriangle(x + 6, y - 8, x + 10, y - 8, x + 8, y - 14);
    }
    if (enemy.confuseTicks > 0) {
      const pulse = 0.72 + Math.sin(this.viewTime * 0.012 + enemy.id) * 0.18;
      graphics.lineStyle(2, 0xe7a14b, pulse);
      graphics.strokeCircle(x, y, 12);
      graphics.fillStyle(0x50351d, 0.9);
      graphics.fillCircle(x - 7, y + 9, 2.5);
      graphics.fillCircle(x + 6, y + 10, 2);
      const headingX = Math.cos(enemy.heading);
      const headingY = Math.sin(enemy.heading);
      graphics.lineStyle(2, 0xffc061, 0.92);
      graphics.lineBetween(x, y, x + headingX * 11, y + headingY * 11);
      graphics.fillStyle(0xffc061, 0.92);
      graphics.fillTriangle(
        x + headingX * 13,
        y + headingY * 13,
        x + headingX * 8 - headingY * 3,
        y + headingY * 8 + headingX * 3,
        x + headingX * 8 + headingY * 3,
        y + headingY * 8 - headingX * 3,
      );
    }
    if (enemy.stoppedTicks > 0) {
      graphics.lineStyle(1, 0x77d8ff, 0.82);
      graphics.strokeCircle(x, y, 13);
      graphics.lineBetween(x - 3, y, x + 3, y);
      graphics.lineBetween(x, y - 3, x, y + 3);
    }
    if (enemy.effect === 'shield') {
      graphics.lineStyle(1, 0x9bbdff, 0.65);
      graphics.strokeCircle(x, y, 14);
    }
    if (enemy.invertedEffect) {
      graphics.lineStyle(1, 0xf07cff, 0.82);
      graphics.strokeCircle(x, y, 15);
      graphics.lineBetween(x - 4, y - 4, x + 4, y + 4);
      graphics.lineBetween(x + 4, y - 4, x - 4, y + 4);
    }
  }

  private syncVisualEvents(events: readonly VisualEvent[]): void {
    events.forEach((event) => {
      if (!this.visualEventRun.accept(event.id)) return;
      this.visualEffects.push({ event, startedAt: this.viewTime, lifetime: this.visualLifetime(event) });
      this.triggerViewKick(event);
    });
    if (this.visualEventRun.size > 128) this.visualEventRun.retain(events.map((event) => event.id));
    this.visualEffects = this.visualEffects.filter((effect) => this.viewTime - effect.startedAt < effect.lifetime);
  }

  private visualLifetime(event: VisualEvent): number {
    if (event.kind === 'fire') return weaponFxFor(event.projectileKind).fireLifetime;
    if (event.kind === 'impact') return event.radius && event.radius >= 60 ? Math.max(weaponFxFor(event.projectileKind).impactLifetime, 420) : weaponFxFor(event.projectileKind).impactLifetime;
    if (event.kind === 'status') return 210;
    return event.ability === 'stop-time' ? 320 : event.ability === 'missile-cast' ? 260 : 260;
  }

  private recoilFor(towerId: number): number {
    const effect = this.visualEffects.find((candidate) => candidate.event.kind === 'fire' && candidate.event.sourceId === towerId);
    if (!effect) return 0;
    const ratio = Math.max(0, 1 - (this.viewTime - effect.startedAt) / effect.lifetime);
    return ratio * 2;
  }

  /** A tiny renderer-only kick gives artillery weight without affecting input or simulation state. */
  private triggerViewKick(event: VisualEvent): void {
    if (event.kind !== 'impact') return;
    const style = weaponFxFor(event.projectileKind).style;
    if (style === 'missile') this.cameras.main.shake(70, 0.0026);
    else if (style === 'cruise') this.cameras.main.shake(58, 0.0022);
    else if (style === 'mortar') this.cameras.main.shake(48, 0.0019);
    else if (style === 'howitzer') this.cameras.main.shake(42, 0.0016);
    else if (style === 'gauss') this.cameras.main.shake(24, 0.001);
  }

  private drawZap(graphics: Phaser.GameObjects.Graphics, fromX: number, fromY: number, toX: number, toY: number, color: number, alpha: number, seed: number, width = 1): void {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const length = Math.hypot(dx, dy) || 1;
    const normalX = -dy / length;
    const normalY = dx / length;
    graphics.lineStyle(width, color, alpha);
    graphics.beginPath();
    graphics.moveTo(fromX, fromY);
    for (let step = 1; step < 4; step += 1) {
      const t = step / 4;
      const wobble = Math.sin(seed * 1.91 + step * 2.67) * 3;
      graphics.lineTo(fromX + dx * t + normalX * wobble, fromY + dy * t + normalY * wobble);
    }
    graphics.lineTo(toX, toY);
    graphics.strokePath();
  }

  private fireDirection(event: VisualEvent): { x: number; y: number; normalX: number; normalY: number } {
    const target = event.target ?? { x: event.position.x, y: event.position.y - 1 };
    const dx = target.x - event.position.x;
    const dy = target.y - event.position.y;
    const length = Math.hypot(dx, dy) || 1;
    const x = dx / length;
    const y = dy / length;
    return { x, y, normalX: -y, normalY: x };
  }

  private drawFireEffect(effect: ActiveVisualEffect, age: number, alpha: number): void {
    const graphics = this.fxGraphics;
    const event = effect.event;
    const color = event.color ?? 0xffe15b;
    const style = weaponFxFor(event.projectileKind).style;
    const { x, y, normalX, normalY } = this.fireDirection(event);
    const source = event.position;
    const target = event.target;
    const flash = Math.max(0, 1 - age / 70) * alpha;

    if (event.projectileKind === 'orbitalStrike' && target) {
      const progress = Math.min(1, age / Math.max(1, effect.lifetime * 0.55));
      const boltY = source.y + (target.y - source.y) * progress;
      graphics.lineStyle(5, 0x7ce9ff, alpha * 0.32);
      graphics.lineBetween(source.x, source.y, target.x, target.y);
      graphics.lineStyle(2, 0xffffff, alpha * 0.92);
      graphics.lineBetween(source.x, source.y, target.x, target.y);
      graphics.fillStyle(0xffffff, alpha);
      graphics.fillCircle(target.x, boltY, 3);
      graphics.lineStyle(1, 0xff5e4c, alpha * 0.9);
      graphics.strokeCircle(target.x, target.y, 8 - progress * 3);
      return;
    }

    if (style === 'tesla') {
      graphics.lineStyle(2, color, alpha * 0.7);
      graphics.strokeCircle(source.x, source.y, 5 + age / 26);
      if (target) this.drawZap(graphics, source.x, source.y, target.x, target.y, color, alpha * 0.9, event.id, 1.5);
      return;
    }
    if (style === 'chain') {
      if (target) {
        this.drawZap(graphics, source.x, source.y, target.x, target.y, color, alpha * 0.92, event.id, 2);
        this.drawZap(graphics, source.x + normalX * 3, source.y + normalY * 3, target.x, target.y, 0xf4f7ba, alpha * 0.45, event.id + 11, 1);
      }
      graphics.fillStyle(color, flash);
      graphics.fillCircle(source.x, source.y, 4);
      return;
    }
    if (style === 'gauss') {
      graphics.lineStyle(2, color, alpha * 0.82);
      graphics.strokeCircle(source.x, source.y, 4 + age / 16);
      graphics.lineStyle(1, 0xffffff, alpha * 0.9);
      graphics.strokeCircle(source.x, source.y, 2 + age / 42);
      if (target) {
        graphics.lineStyle(3, color, alpha * 0.64);
        graphics.lineBetween(source.x, source.y, target.x, target.y);
        graphics.lineStyle(1, 0xffffff, alpha * 0.9);
        graphics.lineBetween(source.x, source.y, target.x, target.y);
      }
      return;
    }
    if (style === 'gatling') {
      if (target) {
        [-2, 0, 2].forEach((offset) => {
          graphics.lineStyle(offset === 0 ? 2 : 1, color, alpha * (offset === 0 ? 0.9 : 0.58));
          graphics.lineBetween(source.x + normalX * offset, source.y + normalY * offset, target.x, target.y);
        });
      }
      graphics.fillStyle(0xfff2ae, flash);
      graphics.fillCircle(source.x + x * 5, source.y + y * 5, 3);
      return;
    }
    if (style === 'aa') {
      [-3, 3].forEach((offset) => {
        const muzzleX = source.x + normalX * offset + x * 5;
        const muzzleY = source.y + normalY * offset + y * 5;
        graphics.fillStyle(0xfff6bf, flash);
        graphics.fillCircle(muzzleX, muzzleY, 2.5);
        if (target) {
          graphics.lineStyle(1, color, alpha * 0.88);
          graphics.lineBetween(muzzleX, muzzleY, target.x, target.y);
        }
      });
      return;
    }
    if (style === 'goo') {
      graphics.fillStyle(0x9ee86b, flash);
      graphics.fillCircle(source.x + x * 5, source.y + y * 5, 4);
      graphics.fillStyle(0xeaffc3, flash * 0.9);
      graphics.fillCircle(source.x + x * 7 + normalX * 2, source.y + y * 7 + normalY * 2, 1.3);
      return;
    }
    if (style === 'oil') {
      [-0.22, 0, 0.22].forEach((spread) => {
        const sprayX = x * Math.cos(spread) - y * Math.sin(spread);
        const sprayY = x * Math.sin(spread) + y * Math.cos(spread);
        const end = target ? Math.min(36, Math.hypot(target.x - source.x, target.y - source.y)) : 28;
        graphics.lineStyle(2, 0xe8a13e, alpha * 0.72);
        graphics.lineBetween(source.x + sprayX * 4, source.y + sprayY * 4, source.x + sprayX * end, source.y + sprayY * end);
      });
      return;
    }
    if (style === 'nulificator') {
      graphics.lineStyle(2, color, alpha * 0.76);
      graphics.strokeCircle(source.x, source.y, 5 + age / 20);
      graphics.lineStyle(1, 0xf0c8ff, alpha * 0.66);
      graphics.strokeCircle(source.x, source.y, 9 + age / 13);
      return;
    }
    if (style === 'cruise') {
      [-0.18, 0.18].forEach((spread) => {
        const exhaustX = x * Math.cos(spread) - y * Math.sin(spread);
        const exhaustY = x * Math.sin(spread) + y * Math.cos(spread);
        graphics.lineStyle(3, 0xff963b, alpha * 0.78);
        graphics.lineBetween(source.x - exhaustX * 3, source.y - exhaustY * 3, source.x - exhaustX * 19, source.y - exhaustY * 19);
      });
      graphics.fillStyle(0xfff0b0, flash);
      graphics.fillCircle(source.x + x * 8, source.y + y * 8, 5);
      graphics.lineStyle(2, 0x9a8770, alpha * 0.46);
      graphics.strokeCircle(source.x - x * 4, source.y - y * 4, 5 + age / 24);
      return;
    }
    if (style === 'mortar') {
      graphics.fillStyle(0xffbf64, flash);
      graphics.fillCircle(source.x + x * 5, source.y + y * 5, 5);
      graphics.lineStyle(2, 0xe2d4bd, alpha * 0.58);
      graphics.strokeCircle(source.x, source.y, 5 + age / 20);
      graphics.lineStyle(2, 0x8e765b, alpha * 0.42);
      graphics.lineBetween(source.x, source.y, source.x + x * 9 - normalX * 5, source.y + y * 9 - normalY * 5);
      graphics.lineBetween(source.x, source.y, source.x + x * 9 + normalX * 5, source.y + y * 9 + normalY * 5);
      return;
    }
    if (style === 'howitzer') {
      graphics.fillStyle(0xffe4a0, flash);
      graphics.fillCircle(source.x + x * 7, source.y + y * 7, 5);
      for (let ray = -1; ray <= 1; ray += 1) {
        graphics.lineStyle(2, ray === 0 ? 0xffb653 : 0xd9863c, alpha * (ray === 0 ? 0.9 : 0.58));
        graphics.lineBetween(
          source.x + x * 4,
          source.y + y * 4,
          source.x + x * 15 + normalX * ray * 5,
          source.y + y * 15 + normalY * ray * 5,
        );
      }
      graphics.lineStyle(2, 0xb8aa94, alpha * 0.4);
      graphics.strokeCircle(source.x - x * 2, source.y - y * 2, 4 + age / 24);
      return;
    }
    const size = style === 'missile' ? 6 : 4;
    graphics.fillStyle(color, flash);
    graphics.fillCircle(source.x + x * size, source.y + y * size, size);
    graphics.lineStyle(2, color, alpha * 0.55);
    graphics.lineBetween(source.x, source.y, source.x + x * 11, source.y + y * 11);
  }

  private drawImpactEffect(effect: ActiveVisualEffect, alpha: number): void {
    const graphics = this.fxGraphics;
    const event = effect.event;
    const color = event.color ?? 0xffe15b;
    const style = weaponFxFor(event.projectileKind).style;
    const { x, y } = event.position;
    const radius = Math.max(7, Math.min(event.radius ?? 12, 100));
    const expansion = 1 - alpha;

    if (style === 'cruise') {
      const blastRadius = radius * (0.24 + expansion * 0.78);
      graphics.fillStyle(0xffb13f, alpha * 0.42);
      graphics.fillCircle(x, y, Math.max(5, blastRadius));
      graphics.lineStyle(3, 0xffefad, alpha * 0.92);
      graphics.strokeCircle(x, y, 5 + blastRadius);
      graphics.lineStyle(2, 0x6b5544, alpha * 0.5);
      graphics.strokeCircle(x, y, 9 + blastRadius * 1.3);
      for (let shard = 0; shard < 10; shard += 1) {
        const angle = shard * Math.PI / 5 + event.id * 0.13;
        const distance = 10 + blastRadius * (0.6 + (shard % 3) * 0.18);
        graphics.fillStyle(shard % 2 ? 0xff8c32 : 0x746256, alpha * 0.78);
        graphics.fillCircle(x + Math.cos(angle) * distance, y + Math.sin(angle) * distance, shard % 2 ? 2.2 : 3);
      }
      return;
    }
    if (style === 'mortar') {
      const blastRadius = radius * (0.2 + expansion * 0.72);
      graphics.fillStyle(0x6b452c, alpha * 0.36);
      graphics.fillEllipse(x, y + 2, Math.max(9, blastRadius * 2), Math.max(5, blastRadius));
      graphics.lineStyle(2, 0xffd179, alpha * 0.9);
      graphics.strokeCircle(x, y, 4 + blastRadius);
      for (let plume = 0; plume < 8; plume += 1) {
        const angle = plume * Math.PI / 4 + event.id * 0.09;
        const distance = 7 + blastRadius * (0.8 + (plume % 2) * 0.3);
        graphics.lineStyle(2, plume % 2 ? 0xa66d3b : 0xffa84d, alpha * 0.7);
        graphics.lineBetween(x + Math.cos(angle) * 3, y + Math.sin(angle) * 3, x + Math.cos(angle) * distance, y + Math.sin(angle) * distance);
      }
      return;
    }
    if (style === 'missile' || style === 'howitzer') {
      const scale = style === 'missile' ? 1 : 0.52;
      graphics.fillStyle(style === 'missile' ? 0xffb44d : 0xff822f, alpha * 0.34);
      graphics.fillCircle(x, y, Math.max(4, radius * scale * (0.16 + expansion * 0.84)));
      graphics.lineStyle(2, 0xffe7a2, alpha * 0.9);
      graphics.strokeCircle(x, y, 4 + radius * scale * expansion);
      for (let shard = 0; shard < 6; shard += 1) {
        const angle = shard * Math.PI / 3 + event.id * 0.17;
        graphics.lineStyle(1, 0xffcf63, alpha * 0.7);
        graphics.lineBetween(x + Math.cos(angle) * 4, y + Math.sin(angle) * 4, x + Math.cos(angle) * (8 + radius * 0.18 * expansion), y + Math.sin(angle) * (8 + radius * 0.18 * expansion));
      }
      return;
    }
    if (style === 'tesla') {
      const zapRadius = radius * Math.min(1, 0.35 + expansion * 0.9);
      graphics.lineStyle(2, color, alpha * 0.72);
      graphics.strokeCircle(x, y, zapRadius);
      for (let branch = 0; branch < 4; branch += 1) {
        const angle = branch * Math.PI / 2 + event.id * 0.21;
        this.drawZap(graphics, x, y, x + Math.cos(angle) * zapRadius, y + Math.sin(angle) * zapRadius, color, alpha * 0.82, event.id + branch, 1.5);
      }
      graphics.fillStyle(0xffffff, alpha * 0.9);
      graphics.fillCircle(x, y, 3);
      return;
    }
    if (style === 'chain') {
      for (let branch = 0; branch < 4; branch += 1) {
        const angle = branch * Math.PI / 2 + event.id * 0.21;
        this.drawZap(graphics, x, y, x + Math.cos(angle) * 14, y + Math.sin(angle) * 14, color, alpha * 0.82, event.id + branch, 1.5);
      }
      graphics.fillStyle(0xffffff, alpha * 0.9);
      graphics.fillCircle(x, y, 3);
      return;
    }
    if (style === 'gauss') {
      graphics.lineStyle(1, color, alpha * 0.62);
      graphics.strokeCircle(x, y, 7 + expansion * 8);
      graphics.lineStyle(1, 0xffffff, alpha * 0.42);
      graphics.strokeCircle(x, y, 4 + expansion * 5);
      graphics.lineStyle(2, color, alpha * 0.9);
      graphics.lineBetween(x - 14, y, x + 14, y);
      graphics.lineBetween(x, y - 14, x, y + 14);
      graphics.fillStyle(0xffffff, alpha * 0.92);
      graphics.fillCircle(x, y, 3 + expansion * 2);
      return;
    }
    if (style === 'gatling') {
      graphics.lineStyle(1, color, alpha * 0.62);
      graphics.strokeCircle(x, y, 4 + expansion * 6);
      graphics.lineStyle(2, 0xfff2ae, alpha * 0.88);
      graphics.lineBetween(x - 6, y - 6, x + 6, y + 6);
      graphics.lineBetween(x + 6, y - 6, x - 6, y + 6);
      graphics.fillStyle(color, alpha * 0.72);
      graphics.fillCircle(x, y, 2 + expansion * 2);
      return;
    }
    if (style === 'aa' || style === 'rocket') {
      graphics.lineStyle(2, style === 'aa' ? 0xfff2b1 : color, alpha * 0.85);
      graphics.lineBetween(x - 5, y - 5, x + 5, y + 5);
      graphics.lineBetween(x + 5, y - 5, x - 5, y + 5);
      graphics.fillStyle(color, alpha * 0.72);
      graphics.fillCircle(x, y, 3 + expansion * 2);
      return;
    }
    if (style === 'goo') {
      graphics.fillStyle(0x85d650, alpha * 0.66);
      graphics.fillCircle(x, y, 5 + expansion * 6);
      for (let blob = 0; blob < 4; blob += 1) {
        const angle = blob * Math.PI / 2 + event.id * 0.12;
        graphics.fillStyle(0xb7ef7c, alpha * 0.78);
        graphics.fillCircle(x + Math.cos(angle) * (6 + expansion * 8), y + Math.sin(angle) * (6 + expansion * 8), 2.2);
      }
      return;
    }
    if (style === 'oil') {
      graphics.lineStyle(2, 0xe8a13e, alpha * 0.78);
      graphics.strokeCircle(x, y, 4 + expansion * 11);
      graphics.fillStyle(0xb7772c, alpha * 0.5);
      graphics.fillCircle(x, y, 4 + expansion * 5);
      return;
    }
    if (style === 'nulificator') {
      graphics.lineStyle(2, color, alpha * 0.88);
      graphics.strokeCircle(x, y, 5 + expansion * 15);
      graphics.lineStyle(1, 0xf0c8ff, alpha * 0.62);
      graphics.strokeCircle(x, y, 10 + expansion * 9);
      return;
    }
    graphics.lineStyle(2, color, alpha * 0.86);
    graphics.strokeCircle(x, y, 4 + radius * expansion);
    graphics.fillStyle(color, alpha * 0.32);
    graphics.fillCircle(x, y, Math.max(3, radius * 0.12));
  }

  private drawAbilityEffect(effect: ActiveVisualEffect, age: number, alpha: number): void {
    const graphics = this.fxGraphics;
    const event = effect.event;
    const color = event.color ?? 0xffe15b;
    if (event.ability === 'missile-cast') {
      const target = event.target ?? event.position;
      const travel = Math.min(1, age / effect.lifetime);
      const x = Phaser.Math.Linear(event.position.x, target.x, travel);
      const y = Phaser.Math.Linear(event.position.y, target.y, travel) - Math.sin(travel * Math.PI) * 34;
      graphics.lineStyle(2, 0xffa748, alpha * 0.62);
      graphics.lineBetween(event.position.x, event.position.y, x, y);
      graphics.fillStyle(0xffe15b, alpha);
      graphics.fillCircle(x, y, 4);
      return;
    }
    if (event.ability === 'mine-impact') {
      const radius = event.radius ?? 35;
      const expansion = 1 - alpha;
      graphics.fillStyle(0xff8a36, alpha * 0.36);
      graphics.fillCircle(event.position.x, event.position.y, 5 + radius * expansion);
      graphics.lineStyle(2, 0xffdf83, alpha * 0.92);
      graphics.strokeCircle(event.position.x, event.position.y, 4 + radius * expansion);
      for (let shard = 0; shard < 8; shard += 1) {
        const angle = shard * Math.PI / 4;
        graphics.lineBetween(
          event.position.x + Math.cos(angle) * 5,
          event.position.y + Math.sin(angle) * 5,
          event.position.x + Math.cos(angle) * (8 + 18 * expansion),
          event.position.y + Math.sin(angle) * (8 + 18 * expansion),
        );
      }
      return;
    }
    if (event.ability === 'mine-placed' || event.ability === 'barbed-wire-placed') {
      const radius = 13 + (1 - alpha) * 8;
      graphics.lineStyle(2, event.ability === 'mine-placed' ? 0xe1c059 : 0xaab0a5, alpha * 0.85);
      graphics.strokeCircle(event.position.x, event.position.y, radius);
      return;
    }
    const radius = event.radius ?? 24;
    graphics.lineStyle(2, color, alpha * 0.85);
    graphics.strokeCircle(event.position.x, event.position.y, radius * (0.7 + (1 - alpha) * 0.3));
  }

  private drawVisualEffects(): void {
    this.visualEffects.forEach((effect) => {
      const age = this.viewTime - effect.startedAt;
      const alpha = Math.max(0, 1 - age / effect.lifetime);
      const event = effect.event;
      if (event.kind === 'fire') {
        this.drawFireEffect(effect, age, alpha);
        return;
      }
      if (event.kind === 'impact') {
        this.drawImpactEffect(effect, alpha);
        return;
      }
      if (event.kind === 'status') {
        const graphics = this.fxGraphics;
        const color = event.color ?? 0xffe15b;
        if (event.status === 'slow' || event.status === 'corrosion' || event.status === 'burning') {
          graphics.fillStyle(event.status === 'burning' ? 0xff6a24 : 0x8cdb59, alpha * 0.34);
          graphics.fillCircle(event.position.x, event.position.y, 7 + age / 38);
          graphics.lineStyle(2, color, alpha * 0.9);
          graphics.strokeCircle(event.position.x, event.position.y, 7 + age / 24);
        } else {
          graphics.lineStyle(2, color, alpha * 0.9);
          graphics.strokeCircle(event.position.x, event.position.y, 7 + age / 24);
        }
        return;
      }
      this.drawAbilityEffect(effect, age, alpha);
    });
  }
}
